/**
 * PatientsList.jsx
 * src/pages/assistant/PatientsList.jsx
 */

import { useState, useEffect } from "react";
import { BSModal, DeleteModal, Avatar, PageHeader, Loading, EmptyState, MiniStat, apiFetch } from "../../components/helpers";

const BLOOD_GROUPS = ["A+","A-","B+","B-","O+","O-","AB+","AB-"];
const GENDERS     = ["Male","Female","Other"];
const EMPTY_FORM  = { patient_id: null, name: "", email: "", mobile: "", dob: "", gender: "Male", blood_group: "", address: "", medical_history: "" };


function ProfilePanel({ patient, onClose }) {
  if (!patient) return null;
  return (
    <>
      <div className="offcanvas offcanvas-end show" style={{ width: 360, visibility: "visible" }}>
        <div className="offcanvas-header border-bottom px-4">
          <h6 className="offcanvas-title fw-bold" style={{ color: "#0A1628" }}>Patient Profile</h6>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>
        <div className="offcanvas-body px-4">
          <div className="d-flex align-items-center gap-3 p-3 rounded-3 mb-4" style={{ background: "#FAECE7", border: "1px solid #F5C4B3" }}>
            <Avatar name={patient.name} size={50} />
            <div>
              <div className="fw-bold" style={{ fontSize: 16, color: "#0A1628" }}>{patient.name}</div>
              <div style={{ fontSize: 12, color: "#D85A30", fontWeight: 600 }}>Patient #{patient.patient_id}</div>
            </div>
          </div>
          <table className="table table-sm table-borderless">
            <tbody>
              {[["Email", patient.email], ["Mobile", patient.mobile], ["Date of Birth", patient.dob], ["Gender", patient.gender], ["Blood Group", patient.blood_group], ["Address", patient.address], ["Medical History", patient.medical_history]].filter(([, v]) => v).map(([k, v]) => (
                <tr key={k} style={{ borderBottom: "1px solid #F0F4F8" }}>
                  <td className="text-muted fw-semibold py-2" style={{ fontSize: 12, width: "42%" }}>{k}</td>
                  <td className="py-2" style={{ fontSize: 13 }}>{k === "Blood Group" ? <span className="badge text-bg-warning">{v}</span> : v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="offcanvas-backdrop fade show" onClick={onClose} />
    </>
  );
}

export default function PatientsList() {
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [modalOpen,   setModalOpen]   = useState(false);
  const [deleteId,    setDeleteId]    = useState(null);
  const [viewPatient, setViewPatient] = useState(null);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");
  const [flash,       setFlash]       = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const json = await apiFetch("get_patient_list.php");
      if (json.status) setItems(json.data || []);
    } catch { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 3000); };
  const openAdd  = () => { setForm(EMPTY_FORM); setError(""); setModalOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setError(""); setModalOpen(true); };

  const handleSubmit = async () => {
    if (!form.name.trim())   return setError("Name is required");
    if (!form.mobile.trim()) return setError("Mobile is required");
    setSubmitting(true); setError("");
    try {
      const isEdit = !!form.patient_id;
      const endpoint = isEdit ? "update_patient.php" : "Register_Patient.php";
      const json = await apiFetch(endpoint, { method: "POST", body: form });
      if (json.status) { setModalOpen(false); showFlash(json.message || "Saved!"); load(); }
      else setError(json.message || "Something went wrong");
    } catch { setError("Network error"); }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    try {
      const json = await apiFetch("delete_patient.php", { method: "POST", body: { patient_id: deleteId } });
      if (json.status) { setDeleteId(null); showFlash("Patient deleted!"); load(); }
    } catch {}
  };

  const filtered = items.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.mobile?.includes(search) || p.email?.toLowerCase().includes(search.toLowerCase())
  );

  const inp = "form-control";

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>
      <PageHeader title="Patient List" subtitle={`${filtered.length} patient${filtered.length !== 1 ? "s" : ""}`}>
        <input className={inp} placeholder="Search by name, mobile, email..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 260, fontSize: 13 }} />
        <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30", borderRadius: 10 }} onClick={openAdd}>+ Add Patient</button>
      </PageHeader>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><MiniStat label="Total Patients" value={items.length} accent="#D85A30" /></div>
        <div className="col-6 col-md-3"><MiniStat label="New This Month" value={items.filter(p => { const d = new Date(p.created_at); return d.getMonth() === new Date().getMonth(); }).length} accent="#1D9E75" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Male" value={items.filter(p => p.gender === "Male").length} accent="#378ADD" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Female" value={items.filter(p => p.gender === "Female").length} accent="#7F77DD" /></div>
      </div>

      {flash && <div className="alert alert-success py-2 px-3 mb-3" style={{ fontSize: 13 }}>{flash}</div>}

      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          {loading ? <Loading /> : filtered.length === 0 ? <EmptyState message="No patients found." /> : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#FAFBFC" }}>
                  <tr>{["Patient","Mobile","Gender","Blood Group","DOB","Actions"].map(h => <th key={h} className="text-muted fw-bold border-0 px-4 py-3" style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => (
                    <tr key={i}>
                      <td className="px-4">
                        <div className="d-flex align-items-center gap-2">
                          <Avatar name={p.name} size={32} />
                          <div><div className="fw-semibold" style={{ fontSize: 13 }}>{p.name}</div><div className="text-muted" style={{ fontSize: 11 }}>{p.email}</div></div>
                        </div>
                      </td>
                      <td className="px-4 text-muted" style={{ fontSize: 12, fontFamily: "monospace" }}>{p.mobile}</td>
                      <td className="px-4 text-muted" style={{ fontSize: 13 }}>{p.gender || "—"}</td>
                      <td className="px-4">{p.blood_group ? <span className="badge text-bg-warning" style={{ fontSize: 11 }}>{p.blood_group}</span> : "—"}</td>
                      <td className="px-4 text-muted" style={{ fontSize: 12 }}>{p.dob || "—"}</td>
                      <td className="px-4">
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm fw-bold" style={{ background: "#FAECE7", color: "#D85A30", fontSize: 11 }} onClick={() => setViewPatient(p)}>View</button>
                          <button className="btn btn-sm fw-bold" style={{ background: "#E6F1FB", color: "#042C53", fontSize: 11 }} onClick={() => openEdit(p)}>Edit</button>
                          <button className="btn btn-sm fw-bold" style={{ background: "#FCEBEB", color: "#501313", fontSize: 11 }} onClick={() => setDeleteId(p.patient_id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <BSModal open={modalOpen} title={form.patient_id ? "Edit Patient" : "Add Patient"} size="modal-lg" onClose={() => setModalOpen(false)}>
        <div className="row g-3">
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>FULL NAME *</label><input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Sharma" /></div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>MOBILE *</label><input className={inp} value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} placeholder="+91 98765 43210" /></div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>EMAIL</label><input type="email" className={inp} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="e.g. priya@email.com" /></div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>DATE OF BIRTH</label><input type="date" className={inp} value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} /></div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>GENDER</label><select className="form-select" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>{GENDERS.map(g => <option key={g}>{g}</option>)}</select></div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>BLOOD GROUP</label><select className="form-select" value={form.blood_group} onChange={e => setForm({ ...form, blood_group: e.target.value })}><option value="">Select</option>{BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}</select></div>
          <div className="col-12"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>ADDRESS</label><input className={inp} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="City, State" /></div>
          <div className="col-12"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>MEDICAL HISTORY / ALLERGIES</label><textarea className={inp} rows={2} value={form.medical_history} onChange={e => setForm({ ...form, medical_history: e.target.value })} placeholder="e.g. Diabetic, allergic to penicillin..." /></div>
        </div>
        {error && <div className="alert alert-danger py-2 px-3 mt-3 mb-0" style={{ fontSize: 12 }}>{error}</div>}
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button className="btn btn-light fw-semibold" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30" }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : "Save Patient"}
          </button>
        </div>
      </BSModal>
      <ProfilePanel patient={viewPatient} onClose={() => setViewPatient(null)} />
      <DeleteModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} message="Delete this patient? All appointment history may be affected." />
    </div>
  );
}